/**
 * Helpers operating on the canonical FareDetailAdditionalService[] and
 * FlightPrice.additionalServices[] arrays — bag/seat/sportsEquipment/
 * service entries committed to a FlightOffer. Pure reads + transforms.
 */

import { apply_markup_to_number } from '@liveflight/utilities/price';
import { get_config } from '@/utilities/commons';
import { traveler_type } from './travelers';

// Generic ancillary types that share the per-traveler-per-segment
// selection shape — everything except `seat`, which has its own
// per-segment dict.
const GENERIC_ANCILLARY_TYPES = [
  'bag',
  'sportsEquipment',
  'service',
  'meal',
  'loungeAccess',
  'disabilityAssistance',
];

// Sentinel value for the "none" row of a mutually-exclusive dropdown —
// matches the additional-bags selector convention.
export const UNSET_ANCILLARY_SELECTION = '-1';

/**
 * Project a single ExtraBag-shaped catalog entry into the per-item shape
 * rendered by additional-bags / additional-services blocks (post-markup
 * priceAmount, resolved icon, primary segmentId). Returns `null` when the
 * traveler isn't eligible, or when the entry is fare-included / free —
 * those are dropped here at map time (seats have their own picker; the
 * ancillary-option "Incluso" branch is kept for any future opt-in caller).
 */
const project_catalog_item = (
  entry,
  traveler_id,
  flight_offer,
  default_icon
) => {
  if (!entry) return null;
  const {
    id = '',
    travelerIds = [],
    segmentIds: [segmentId = ''] = [],
    price = {},
    name,
    subtitle,
    icon,
    incompatibleWith = [],
    combineWith = [],
    purchaseQuantity = 1,
    included = false,
  } = entry;
  if (!travelerIds.includes(String(traveler_id))) return null;
  // Non-sellable rows (fare-included or free) are dropped so only purchasable
  // ancillaries reach this picker. Opt out per brand via
  // guiSettings.customizations.dropIncludedAncillaries (default: drop).
  const drop_non_sellable = get_config(
    'guiSettings.customizations.dropIncludedAncillaries',
    true
  );
  if (drop_non_sellable && (included || Number(price.total || 0) === 0)) {
    return null;
  }
  return {
    id,
    segmentId,
    title: name,
    subtitle,
    icon: icon || default_icon,
    priceAmount: apply_markup_to_number(flight_offer, price.total || '0'),
    currencySymbol: price.currencySymbol || '',
    incompatibleWith,
    combineWith,
    purchaseQuantity,
  };
};

/**
 * Split projected items (already scoped to one segment + one traveler) into
 * mutually-exclusive groups and standalone stacking toggles.
 *
 * Exclusivity comes from each catalog entry's `incompatibleWith` (a list of
 * canonical ancillary ids): two items are linked when either lists the
 * other's id. Connected components of that relation with 2+ members form a
 * single-select group (rendered as a dropdown row — picking one clears the
 * rest, e.g. Wizzair's 10/20/26/32 kg checked bags). Singletons stack
 * independently (rendered as boolean switches, e.g. wizz-flex / wizz-priority
 * / check-in). Groups are sorted cheapest-first.
 *
 * @param {Array<object>} items projected items carrying `compound_key`, `id`,
 *   `incompatibleWith`, `priceAmount`
 * @returns {{exclusive_groups: Array<Array<object>>, toggles: Array<object>}}
 */
export const partition_exclusive_groups = (items) => {
  const list = Array.isArray(items) ? items : [];
  const keys_by_id = {};
  const item_by_key = {};
  const adjacency = {};
  list.forEach((item) => {
    item_by_key[item.compound_key] = item;
    adjacency[item.compound_key] = new Set();
    if (!keys_by_id[item.id]) keys_by_id[item.id] = [];
    keys_by_id[item.id].push(item.compound_key);
  });
  list.forEach((item) => {
    const { incompatibleWith = [] } = item;
    incompatibleWith.forEach((ref_id) => {
      (keys_by_id[ref_id] || []).forEach((ref_key) => {
        if (ref_key === item.compound_key) return;
        adjacency[item.compound_key].add(ref_key);
        adjacency[ref_key].add(item.compound_key);
      });
    });
  });
  const visited = {};
  const exclusive_groups = [];
  const toggles = [];
  list.forEach((item) => {
    if (visited[item.compound_key]) return;
    const component = [];
    const stack = [item.compound_key];
    while (stack.length) {
      const key = stack.pop();
      if (visited[key]) continue;
      visited[key] = true;
      component.push(item_by_key[key]);
      adjacency[key].forEach((neighbor) => {
        if (!visited[neighbor]) stack.push(neighbor);
      });
    }
    if (component.length >= 2) {
      component.sort(
        (item1, item2) =>
          Number(item1.priceAmount || 0) - Number(item2.priceAmount || 0)
      );
      exclusive_groups.push(component);
    } else {
      toggles.push(component[0]);
    }
  });
  return { exclusive_groups, toggles };
};

// Ids the offer doesn't know sort last, keeping catalog order (stable sort).
const journey_rank = (journey_ids, segment_id) => {
  let rank = journey_ids.indexOf(segment_id);
  if (rank < 0) rank = journey_ids.length;
  return rank;
};

/**
 * Reshape an ExtraBag-shaped catalog (kyte's bags / sportsEquipment /
 * services slices) into a Segment → Traveler grid, with each traveler's
 * items pre-partitioned into mutually-exclusive groups (dropdown rows) and
 * stacking toggles (boolean switches). Segments keep first-appearance order;
 * travelers with nothing to offer on a segment are dropped, as are segments
 * that end up empty. Each leaf item carries its post-markup `priceAmount` so
 * the renderer doesn't have to know about markup math.
 *
 * @param {object} catalog catalog slice keyed by compound_key
 * @param {Array<object>} travelers
 * @param {object} flight_offer
 * @param {string} default_icon
 * @returns {Array<{segmentId: string, travelers: Array<object>}>}
 */
export const group_catalog_by_segment_and_traveler = (
  catalog,
  travelers,
  flight_offer,
  default_icon
) => {
  const safe_catalog = catalog || {};
  const compound_keys = Object.keys(safe_catalog);
  const segment_ids = [];
  compound_keys.forEach((compound_key) => {
    const { segmentIds: [segmentId = ''] = [] } =
      safe_catalog[compound_key] || {};
    if (segmentId && segment_ids.indexOf(segmentId) < 0) {
      segment_ids.push(segmentId);
    }
  });
  // Kyte's catalog key order carries no journey meaning (easyJet can ship the
  // return leg first): rank rows by the offer's own segment order.
  const { itineraries = [] } = flight_offer || {};
  const journey_ids = itineraries.flatMap(({ segments = [] } = {}) =>
    segments.map(({ id }) => id)
  );
  segment_ids.sort(
    (a, b) => journey_rank(journey_ids, a) - journey_rank(journey_ids, b)
  );
  return segment_ids
    .map((segmentId) => ({
      segmentId,
      travelers: (travelers || [])
        .map((traveler) => {
          const items = compound_keys
            .map((compound_key) => {
              const projected = project_catalog_item(
                safe_catalog[compound_key],
                traveler.id,
                flight_offer,
                default_icon
              );
              if (!projected || projected.segmentId !== segmentId) return null;
              return {
                compound_key,
                member_keys: [compound_key],
                ...projected,
              };
            })
            .filter(Boolean);
          const { exclusive_groups, toggles } =
            partition_exclusive_groups(items);
          return { ...traveler, exclusive_groups, toggles };
        })
        .filter(
          (traveler) =>
            traveler.exclusive_groups.length > 0 || traveler.toggles.length > 0
        ),
    }))
    .filter((segment) => segment.travelers.length > 0);
};

// Synthetic segmentId for the single journey-wide bucket the grouped builder
// emits — the renderer keys off it to suppress the per-leg segment label.
export const GROUPED_SEGMENT_SENTINEL = '__all_segments__';

/**
 * Canonical id for a cross-segment pair: the return-leg twin's id without a
 * trailing `_RT` marker (easyJet `LUG_RT` → `LUG`). Only drives the grouped
 * item's `id` / `incompatibleWith` matching in partition_exclusive_groups;
 * Wizzair ids end in `count=N`, so the strip is a no-op there.
 * @param {string} id
 * @returns {string}
 */
export const canonical_pair_id = (id) => String(id || '').replace(/_RT$/, '');

/**
 * Partition catalog keys into cross-segment equivalence classes — one class
 * per product offered across the journey. Two keys join a class when they
 * share the same `id` (Wizzair repeats one id per segment; the fanned easyJet
 * WGT/FXP/sports rows do too) OR when they are symmetric `combineWith` twins
 * on disjoint segments with the same name (easyJet `LUG`↔`LUG_RT`). The
 * asymmetric complementary link (`WGT.combineWith=[LUG,LUG_RT]`, where the
 * hold bags don't list WGT back) is deliberately NOT unioned.
 *
 * @param {object} catalog catalog slice keyed by compound_key
 * @returns {Array<string[]>} classes of compound_keys, in first-appearance order
 */
export const pair_catalog_classes = (catalog) => {
  const safe = catalog || {};
  const keys = Object.keys(safe);
  const parent = {};
  keys.forEach((key) => {
    parent[key] = key;
  });
  const find = (key) => {
    let root = key;
    while (parent[root] !== root) root = parent[root];
    let cursor = key;
    while (parent[cursor] !== root) {
      const next = parent[cursor];
      parent[cursor] = root;
      cursor = next;
    }
    return root;
  };
  const union = (a, b) => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent[rb] = ra;
  };

  const keys_by_id = {};
  keys.forEach((key) => {
    const { id = '' } = safe[key] || {};
    if (!keys_by_id[id]) keys_by_id[id] = [];
    keys_by_id[id].push(key);
  });
  // (1) identical id across segments
  Object.keys(keys_by_id).forEach((id) => {
    const group = keys_by_id[id];
    for (let i = 1; i < group.length; i += 1) union(group[0], group[i]);
  });
  // (2) symmetric combineWith twins (different id, disjoint segments, same name)
  keys.forEach((key) => {
    const {
      id = '',
      name = '',
      combineWith = [],
      segmentIds = [],
    } = safe[key] || {};
    combineWith.forEach((ref_id) => {
      (keys_by_id[ref_id] || []).forEach((ref_key) => {
        const ref = safe[ref_key] || {};
        const symmetric = (ref.combineWith || []).includes(id);
        const same_name = name && ref.name === name;
        const disjoint = !(segmentIds || []).some((segmentId) =>
          (ref.segmentIds || []).includes(segmentId)
        );
        if (symmetric && same_name && disjoint) union(key, ref_key);
      });
    });
  });

  const order = [];
  const members = {};
  keys.forEach((key) => {
    const root = find(key);
    if (!members[root]) {
      members[root] = [];
      order.push(root);
    }
    members[root].push(key);
  });
  return order.map((root) => members[root]);
};

// Fixed-decimal places a markup-formatted price string carries (e.g. "20.00"
// → 2), so a summed grouped price keeps the same formatting as its members.
const price_decimals = (amount) => {
  const fraction = String(amount).split('.')[1];
  return fraction ? fraction.length : 0;
};

/**
 * Build one grouped picker item from a cross-segment class of catalog keys for
 * a single traveler — or null when the traveler can't take it on every leg.
 * Members are projected through project_catalog_item (markup, free / included /
 * ineligibility drops); the class qualifies only if the surviving members
 * cover every segment in `all_segments`. `priceAmount` sums the members'
 * already-markup-applied prices; `member_keys` are the real per-segment keys
 * the pick/clear handlers write, so booking stays segment-keyed.
 */
const build_grouped_item = (
  member_keys,
  catalog,
  traveler_id,
  flight_offer,
  default_icon,
  all_segments
) => {
  const projected = member_keys
    .map((compound_key) => {
      const item = project_catalog_item(
        catalog[compound_key],
        traveler_id,
        flight_offer,
        default_icon
      );
      return item ? { compound_key, ...item } : null;
    })
    .filter(Boolean);
  if (!projected.length) return null;

  const covered = new Set();
  projected.forEach((item) => {
    const { segmentIds = [] } = catalog[item.compound_key] || {};
    segmentIds.forEach((segmentId) => covered.add(segmentId));
  });
  const covers_all = [...all_segments].every((segmentId) =>
    covered.has(segmentId)
  );
  if (!covers_all) return null;

  const representative = projected[0];
  const canonical_id = canonical_pair_id(representative.id);
  const total = projected.reduce(
    (sum, item) => sum + Number(item.priceAmount || 0),
    0
  );
  return {
    compound_key: `§group:${canonical_id}`,
    member_keys: projected.map((item) => item.compound_key),
    id: canonical_id,
    title: representative.title,
    subtitle: representative.subtitle,
    icon: representative.icon,
    priceAmount: total.toFixed(price_decimals(representative.priceAmount)),
    currencySymbol: representative.currencySymbol,
    incompatibleWith: (representative.incompatibleWith || []).map(
      canonical_pair_id
    ),
    combineWith: (representative.combineWith || []).map(canonical_pair_id),
    purchaseQuantity: representative.purchaseQuantity,
  };
};

/**
 * Grouped variant of group_catalog_by_segment_and_traveler: collapse each
 * product's per-segment rows into ONE journey-wide choice per traveler. Same
 * return shape as the segment builder, but a single synthetic bucket
 * (segmentId = GROUPED_SEGMENT_SENTINEL). Rendering is unchanged — exclusive
 * classes still collapse to a selector, the rest stack as toggles — and the
 * selection contract is untouched: each grouped item's `member_keys` are the
 * real per-segment compound_keys, so pick/clear write the same segment-keyed
 * state the book path already consumes.
 *
 * @param {object} catalog catalog slice keyed by compound_key
 * @param {Array<object>} travelers
 * @param {object} flight_offer
 * @param {string} default_icon
 * @returns {Array<{segmentId: string, travelers: Array<object>}>}
 */
export const group_catalog_by_traveler = (
  catalog,
  travelers,
  flight_offer,
  default_icon
) => {
  const safe_catalog = catalog || {};
  const classes = pair_catalog_classes(safe_catalog);
  const all_segments = new Set();
  Object.keys(safe_catalog).forEach((key) => {
    const { segmentIds = [] } = safe_catalog[key] || {};
    segmentIds.forEach((segmentId) => all_segments.add(segmentId));
  });

  const grouped_travelers = (travelers || [])
    .map((traveler) => {
      const items = classes
        .map((member_keys) =>
          build_grouped_item(
            member_keys,
            safe_catalog,
            traveler.id,
            flight_offer,
            default_icon,
            all_segments
          )
        )
        .filter(Boolean);
      const { exclusive_groups, toggles } = partition_exclusive_groups(items);
      return { ...traveler, exclusive_groups, toggles };
    })
    .filter(
      (traveler) =>
        traveler.exclusive_groups.length > 0 || traveler.toggles.length > 0
    );

  if (!grouped_travelers.length) return [];
  return [
    { segmentId: GROUPED_SEGMENT_SENTINEL, travelers: grouped_travelers },
  ];
};

/**
 * Build <selector> options for one mutually-exclusive group: a leading
 * "none" row plus one row per item, cheapest-first, each showing the
 * post-markup price. Mirrors the additional-bags dropdown row markup.
 *
 * @param {Array<object>} group_items projected items in the group
 * @param {string} none_label copy for the deselect row
 * @returns {Array<object>} selector options
 */
export const build_selector_options = (group_items, none_label) => {
  const list = Array.isArray(group_items) ? group_items : [];
  const options = [
    {
      value: UNSET_ANCILLARY_SELECTION,
      label: none_label,
      total_price: 0,
      icon: { name: 'luggage' },
      html: none_label,
    },
  ];
  list.forEach((item) => {
    const price_glyph = item.currencySymbol || '';
    options.push({
      value: item.compound_key,
      label: item.title,
      total_price: Number(item.priceAmount || 0),
      // `--` prefix → vb-icon renders it as var(--success).
      icon: { name: 'check_circle', color: '--success' },
      html: `<div class="d-flex justify-content-between"><span>${item.title}</span><span class="semibold text-primary">${item.priceAmount}${price_glyph}</span></div>`,
    });
  });
  return options;
};

/**
 * Apply a mutator to one traveler's per-traveler bucket inside a canonical
 * selections object `{ [traveler_id]: { [compound_key]: qty } }`, returning
 * a fresh top-level value suitable for emitting to a v-model `input`
 * listener. Mutator receives the per-traveler bucket clone.
 */
export const update_traveler_selection = (value, traveler_id, mutator) => {
  const next = { ...(value || {}) };
  const per_traveler = { ...(next[traveler_id] || {}) };
  mutator(per_traveler);
  next[traveler_id] = per_traveler;
  return next;
};

/**
 * Catalog keys carrying canonical ancillary id `ref`, optionally restricted
 * to `segment_set` (a Set of segment ids). incompatibleWith / combineWith
 * reference bare canonical ids (no segment), and the same id is offered on
 * every segment — Wizzair lists `checked-in-bag-20|count=1` on both the
 * outbound and the return leg. Scoping the resolution to the picked entry's
 * own segment(s) is what keeps a change on one leg from rewriting the other;
 * an unscoped resolve would clear the conflicting bag on BOTH legs. An empty
 * / absent `segment_set` means "no scoping" (degenerate entries with no
 * segment info behave as before).
 * @param {object} catalog catalog slice keyed by compound_key
 * @param {string} ref bare canonical ancillary id
 * @param {Set<string>} [segment_set]
 * @returns {string[]} matching compound_keys
 */
export const catalog_keys_for_id = (catalog, ref, segment_set) => {
  const safe = catalog || {};
  return Object.keys(safe).filter((key) => {
    const entry = safe[key] || {};
    if (entry.id !== ref) return false;
    if (!segment_set || !segment_set.size) return true;
    const { segmentIds = [] } = entry;
    return segmentIds.some((segmentId) => segment_set.has(segmentId));
  });
};

/**
 * Compound keys a single pick/clear should touch. Normally just the key
 * itself; an entry flagged `requiredOnAllSegments` fans to every key sharing
 * its canonical id so the bag lands on (or leaves) every segment at once.
 * @param {object} catalog
 * @param {string} compound_key
 * @returns {string[]}
 */
export const keys_to_set_for_pick = (catalog, compound_key) => {
  const safe = catalog || {};
  const { id: entry_id, requiredOnAllSegments = false } =
    safe[compound_key] || {};
  if (!requiredOnAllSegments) return [compound_key];
  return Object.keys(safe).filter((key) => (safe[key] || {}).id === entry_id);
};

// easyJet sells `LUS` + `LUS_RT` as one unit: a requiredOnAllSegments entry's
// combineWith partners live on the other legs, so they resolve unscoped.
const combine_scope = (segment_set, required_on_all_segments) => {
  let scope = segment_set;
  if (required_on_all_segments) scope = undefined;
  return scope;
};

const segment_set_for_keys = (catalog, keys) => {
  const safe = catalog || {};
  const set = new Set();
  keys.forEach((key) => {
    const { segmentIds = [] } = safe[key] || {};
    segmentIds.forEach((segmentId) => set.add(segmentId));
  });
  return set;
};

/**
 * Select `compound_key` in a per-traveler selection bucket (mutates in
 * place). Sets the key (and its requiredOnAllSegments siblings), clears
 * every incompatibleWith conflict, and recurses into combineWith partners.
 * incompatibleWith / combineWith resolution is scoped to the segment(s) the
 * pick actually touches, so picking a bag on the return leg never disturbs a
 * different bag already chosen on the outbound leg. `visited` guards a
 * possible A↔B combineWith cycle.
 * @param {object} catalog
 * @param {object} per_traveler `{ [compound_key]: qty }` — mutated
 * @param {string} compound_key
 * @param {object} [visited]
 */
export const apply_pick_selection = (
  catalog,
  per_traveler,
  compound_key,
  visited = {}
) => {
  const safe = catalog || {};
  if (visited[compound_key]) return;
  visited[compound_key] = true;
  const set_keys = keys_to_set_for_pick(safe, compound_key);
  set_keys.forEach((key) => {
    per_traveler[key] = 1;
  });
  const segment_set = segment_set_for_keys(safe, set_keys);
  const {
    incompatibleWith = [],
    combineWith = [],
    requiredOnAllSegments = false,
  } = safe[compound_key] || {};
  incompatibleWith.forEach((ref) => {
    catalog_keys_for_id(safe, ref, segment_set).forEach((key) => {
      delete per_traveler[key];
    });
  });
  combineWith.forEach((ref) => {
    catalog_keys_for_id(
      safe,
      ref,
      combine_scope(segment_set, requiredOnAllSegments)
    ).forEach((key) => apply_pick_selection(safe, per_traveler, key, visited));
  });
};

/**
 * Deselect `compound_key` (and its requiredOnAllSegments siblings) from a
 * per-traveler bucket (mutates in place), cascading to combineWith partners
 * on the same segment(s). Mirror of apply_pick_selection.
 * @param {object} catalog
 * @param {object} per_traveler mutated
 * @param {string} compound_key
 * @param {object} [visited]
 */
export const apply_clear_selection = (
  catalog,
  per_traveler,
  compound_key,
  visited = {}
) => {
  const safe = catalog || {};
  if (visited[compound_key]) return;
  visited[compound_key] = true;
  const set_keys = keys_to_set_for_pick(safe, compound_key);
  set_keys.forEach((key) => {
    delete per_traveler[key];
  });
  const segment_set = segment_set_for_keys(safe, set_keys);
  const { combineWith = [], requiredOnAllSegments = false } =
    safe[compound_key] || {};
  combineWith.forEach((ref) => {
    catalog_keys_for_id(
      safe,
      ref,
      combine_scope(segment_set, requiredOnAllSegments)
    ).forEach((key) => apply_clear_selection(safe, per_traveler, key, visited));
  });
};

/**
 * Italian label for an additional-service line item rendered in the
 * resume sidebar / receipt. Accepts both the legacy uppercase Amadeus
 * vocabulary (CHECKED_BAGS / SEATS / MEALS) and the canonical lowercase
 * types (bag / seat / sportsEquipment / service) emitted by the new
 * providers — callers pass whichever they have.
 * @param {string} service_type
 * @param {number} [service_quantity=1]
 * @returns {string}
 */
export function transcode_additional_services(
  service_type,
  service_quantity = 1
) {
  const plural = service_quantity > 1;
  switch (service_type) {
    case 'CHECKED_BAGS':
    case 'bag':
      return plural ? 'bagagli extra in stiva' : 'bagaglio extra in stiva';
    case 'MEALS':
    case 'meal':
      return plural ? 'pasti' : 'pasto';
    case 'SEATS':
    case 'seat':
      return plural ? 'posti a sedere' : 'posto a sedere';
    case 'SPORTS_EQUIPMENT':
    case 'sportsEquipment':
      return plural ? 'attrezzature sportive' : 'attrezzatura sportiva';
    case 'SERVICES':
    case 'service':
      return plural ? 'servizi extra' : 'servizio extra';
    case 'LOUNGE_ACCESS':
    case 'loungeAccess':
      return 'accesso lounge';
    case 'DISABILITY_ASSISTANCE':
    case 'disabilityAssistance':
      return 'assistenza speciale';
    case 'AUTOMATED_CHECK_IN':
    case 'automatedCheckIn':
      return 'check-in automatico';
    default:
      return 'altri servizi';
  }
}

/**
 * Tooltip copy for the additional-service line item, by service type.
 * @param {string} service_type
 * @returns {string}
 */
export function get_additional_service_tooltip(service_type) {
  switch (service_type) {
    case 'CHECKED_BAGS':
    case 'bag':
      return 'Sono i bagagli aggiunti e non inclusi nella tariffa iniziale.';
    default:
      return '';
  }
}

/**
 * FlightPrice.additionalServices entries with a non-negative price —
 * committed paid extras, vs roll-up entries that carry only a type.
 * Defensive: returns [] on any missing piece.
 * @param {object} flight_offer
 * @returns {Array<object>}
 */
export const get_paid_additional_services = (flight_offer) => {
  const { price: { additionalServices = [] } = {} } = flight_offer || {};
  if (!Array.isArray(additionalServices)) return [];
  return additionalServices.filter((addition) => {
    const { price = {} } = addition || {};
    const amount = price.total || price.grandTotal;
    return Number(amount || '-1') >= 0;
  });
};

/**
 * Wipe every committed additional-service mark off a canonical
 * FlightOffer and collapse `price.grandTotal` back to the fare-only
 * `price.total`. Avoids double-counting committed ancillaries against
 * the page-side `uncommitted_additions` re-seeded by
 * build_committed_selections. Clears:
 *   1. `price.additionalServices` (offer-level roll-up)
 *   2. `travelerPricings[].fareDetailsBySegment[].additionalServices`
 *      (per-(pax, segment) entries)
 *   3. `price.grandTotal` ← `price.total` (no-op at search time, where
 *      they're equal).
 * @param {object} flight_offer
 * @returns {object}
 */
export const strip_offer_additional_services = (flight_offer) => {
  if (!flight_offer) return flight_offer;
  const { price = {}, travelerPricings = [] } = flight_offer;
  const fare_only_total = price.total || price.grandTotal || '0';
  return {
    ...flight_offer,
    price: {
      ...price,
      grandTotal: fare_only_total,
      additionalServices: [],
    },
    travelerPricings: travelerPricings.map((pricing) => {
      const { fareDetailsBySegment = [] } = pricing || {};
      return {
        ...pricing,
        fareDetailsBySegment: fareDetailsBySegment.map((fare_detail) => ({
          ...(fare_detail || {}),
          additionalServices: [],
        })),
      };
    }),
  };
};

/**
 * Drop catalog entries already bundled with the chosen fare. Caller
 * passes the SUBSET of committed ancillaries flagged `included: true`;
 * catalog entries whose canonical `id` matches an included entry of the
 * same type are dropped (a Plus fare bundling 2× 20kg bags should not
 * also offer BBG paid). Seats are skipped — the picker handles those.
 * Slices that end up empty are hidden by `steps()` downstream.
 * @param {object} envelope Catalog envelope keyed by canonical type
 * @param {Array<{type: string, id: string}>} included_ancillaries
 * @returns {object}
 */
export const filter_catalog_by_included = (envelope, included_ancillaries) => {
  const safe = envelope || {};
  const list = Array.isArray(included_ancillaries) ? included_ancillaries : [];
  const skip_by_type = {};
  list.forEach((entry) => {
    const { type = '', id = '' } = entry || {};
    if (!type || !id) return;
    if (!skip_by_type[type]) skip_by_type[type] = new Set();
    skip_by_type[type].add(id);
  });
  if (!Object.keys(skip_by_type).length) return safe;
  const next = { ...safe };
  GENERIC_ANCILLARY_TYPES.forEach((type) => {
    const skip = skip_by_type[type];
    if (!skip || !skip.size) return;
    const slice = safe[type];
    if (!slice || typeof slice !== 'object') return;
    // easyJet includes `LUG` + `LUG_RT` as one unit: its twins on the other
    // legs are just as included.
    Object.keys(slice).forEach((compound_key) => {
      const {
        id = '',
        requiredOnAllSegments = false,
        combineWith = [],
      } = slice[compound_key] || {};
      if (requiredOnAllSegments && skip.has(id)) {
        combineWith.forEach((ref) => skip.add(ref));
      }
    });
    const filtered = {};
    Object.keys(slice).forEach((compound_key) => {
      const item = slice[compound_key];
      const { id: item_id = '' } = item || {};
      if (item_id && skip.has(item_id)) return;
      filtered[compound_key] = item;
    });
    next[type] = filtered;
  });
  return next;
};

/**
 * Why a seatmap envelope can't service the seat step, or '' when it can.
 * Reason strings are the ones /seat-selection emitted to rybbit, kept
 * verbatim so the analytics series stays continuous.
 *
 * `mandatory` tightens two checks: a fare requiring a pick for every
 * passenger on every segment has no forward path when ANY deck is disabled,
 * while an optional one only fails on a fully-disabled map.
 *
 * @param {Object} seatmap
 * @param {boolean} [mandatory=false]
 * @returns {string} reason, or '' when the seatmap is usable
 */
export const diagnose_seatmap = (seatmap, mandatory = false) => {
  const {
    empty = false,
    isValid = true,
    canAllPassengersSelectOneSeat = true,
    mappedDecks = {},
  } = seatmap || {};
  const decks = Object.values(mappedDecks);
  if (!seatmap || empty || !decks.length) {
    return mandatory ? 'Missing seat map data' : 'Missing seat map';
  }
  if (!isValid) return 'Incomplete traveler pricing';
  const disabled = decks.filter((deck) => {
    const { segment = {} } = deck || {};
    return segment.disabled === true;
  });
  if (!mandatory) {
    return disabled.length === decks.length ? 'All decks are disabled' : '';
  }
  if (!canAllPassengersSelectOneSeat) {
    return 'Some travelers cannot choose a seat in some decks';
  }
  return disabled.length ? 'Some decks are disabled' : '';
};

/**
 * Derive the wizard-facing traveler list from a canonical FlightOffer
 * (for type) and the form's `traveler_details` (for name + DOB +
 * gender), keyed by travelerId. Output drives the per-pax columns in
 * additional-seats / additional-ancillaries.
 * @param {object} flight_offer
 * @param {Array<object>} traveler_details
 * @returns {Array<{id: string, type: string, firstname: string, middlename: string, lastname: string, birth: string, gender: string}>}
 */
export const derive_travelers = (flight_offer, traveler_details) => {
  const { travelerPricings = [] } = flight_offer || {};
  const details = Array.isArray(traveler_details) ? traveler_details : [];
  const detail_by_id = details.reduce((acc, detail) => {
    const safe = detail || {};
    if (safe.id) acc[safe.id] = safe;
    return acc;
  }, {});
  return travelerPricings.map((pricing) => {
    const { travelerId = '' } = pricing || {};
    const {
      firstname = '',
      middlename = '',
      lastname = '',
      birth = '',
      gender = '',
    } = detail_by_id[travelerId] || {};
    return {
      id: travelerId,
      type: traveler_type(pricing),
      firstname,
      middlename,
      lastname,
      birth,
      gender,
    };
  });
};

/**
 * Build the resume-sidebar entry for the seat step from the canonical
 * `selections.seat` shape `{ [segmentId]: { [travelerId]: SeatEntry } }`.
 * Raw `total` only — the resume applies markup at render; using
 * `grandTotal` (already marked-up on per-seat blocks) would double-apply.
 * @param {object} seat_selections
 * @returns {{type: 'SEATS', amount: number, info: object} | null}
 */
export const build_uncommitted_seat_entry = (seat_selections) => {
  const safe = seat_selections || {};
  const info = {};
  let amount = 0;
  Object.keys(safe).forEach((segment_id) => {
    const per_pax = safe[segment_id] || {};
    Object.keys(per_pax).forEach((traveler_id) => {
      const seat_entry = per_pax[traveler_id] || {};
      const {
        chargeableSeatNumber,
        number,
        seatNumber,
        pricingByTraveler = {},
        price: entry_price = {},
      } = seat_entry;
      const resolved_seat_number = seatNumber || chargeableSeatNumber || number;
      if (!resolved_seat_number) return;
      const { price: pax_price = {}, traveler_name = '' } =
        pricingByTraveler[traveler_id] || {};
      const unit_amount =
        Number(pax_price.total) || Number(entry_price.total) || 0;
      if (!info[segment_id]) info[segment_id] = [];
      info[segment_id].push({
        seatNumber: resolved_seat_number,
        travelerId: traveler_id,
        traveler_name,
      });
      amount += unit_amount;
    });
  });
  if (!Object.keys(info).length) return null;
  return { type: 'SEATS', amount, info };
};

/**
 * Build the resume-sidebar entry for a non-seat step (bag /
 * sportsEquipment / service) from the per-step selection slice and the
 * matching catalog (`ancillaries[step_id]`). `step_selections` shape:
 * `{ [travelerId]: { [compound_key]: quantity } }`. Raw `total` only —
 * markup is applied at render in resume.vue.
 * @param {object} step_selections
 * @param {string} type Canonical AdditionalService type (CHECKED_BAGS, …)
 * @param {object} catalog Catalog slice keyed by compound_key
 * @returns {{type: string, amount: number, info: object} | null}
 */
export const build_uncommitted_step_entry = (
  step_selections,
  type,
  catalog
) => {
  const per_traveler = step_selections || {};
  const safe_catalog = catalog || {};
  const info = {};
  let amount = 0;
  Object.keys(per_traveler).forEach((traveler_id) => {
    const picks = per_traveler[traveler_id] || {};
    Object.keys(picks).forEach((compound_key) => {
      const quantity = Number(picks[compound_key]) || 0;
      if (quantity <= 0) return;
      const entry = safe_catalog[compound_key];
      if (!entry) return;
      const description = entry.name || entry.id || compound_key;
      const { price = {} } = entry;
      const unit_amount = Number(price.total) || 0;
      if (!info[compound_key]) {
        info[compound_key] = {
          description,
          icon: entry.icon || 'luggage',
          content: 0,
        };
      }
      info[compound_key].content += quantity;
      amount += unit_amount * quantity;
    });
  });
  if (!Object.keys(info).length) return null;
  return { type, amount, info };
};

/**
 * Project the canonical `selections` map into the flat array shape
 * BookAncillaries / compute_ancillary_diff consume:
 * `{ travelerId, type, id, quantity, flightSegments }[]`. Bag-shaped
 * picks split their compound_key on ":" to recover `(item_id, segment_id)`;
 * seats walk the per-segment per-pax dict.
 * @param {{bag?: object, seat?: object, sportsEquipment?: object, service?: object}} selections
 * @returns {Array<{travelerId: string, type: string, id: string, quantity: number, flightSegments: string[]}>}
 */
export const flatten_selections = (selections) => {
  const safe = selections || {};
  const out = [];
  GENERIC_ANCILLARY_TYPES.forEach((type) => {
    const per_traveler = safe[type] || {};
    Object.keys(per_traveler).forEach((traveler_id) => {
      const picks = per_traveler[traveler_id] || {};
      Object.keys(picks).forEach((compound_key) => {
        const quantity = Number(picks[compound_key]) || 0;
        if (quantity <= 0) return;
        // Split from the right: ids can contain colons (Kyte format),
        // segment id is always the trailing token.
        const split_at = compound_key.lastIndexOf(':');
        const item_id =
          split_at >= 0 ? compound_key.slice(0, split_at) : compound_key;
        const segment_id =
          split_at >= 0 ? compound_key.slice(split_at + 1) : '';
        out.push({
          travelerId: traveler_id,
          type,
          id: item_id,
          quantity,
          flightSegments: segment_id ? [segment_id] : [],
        });
      });
    });
  });
  const seat_selections = safe.seat || {};
  Object.keys(seat_selections).forEach((segment_id) => {
    const per_pax = seat_selections[segment_id] || {};
    Object.keys(per_pax).forEach((traveler_id) => {
      const { seatNumber, chargeableSeatNumber, number } =
        per_pax[traveler_id] || {};
      const seat_id = seatNumber || chargeableSeatNumber || number;
      if (!seat_id) return;
      out.push({
        travelerId: traveler_id,
        type: 'seat',
        id: seat_id,
        quantity: 1,
        flightSegments: [segment_id],
      });
    });
  });
  return out;
};

/**
 * Split a multi-segment committed entry into one entry per segment. A
 * retrieve can return a fare-bundle component as a single journey-wide row
 * while build_committed_selections always fans out per segment; atomizing
 * both sides lets them match. Single-segment entries atomize to themselves.
 * @param {object} entry
 * @returns {Array<object>}
 */
const segment_atoms = (entry) => {
  const segments = Array.isArray(entry.flightSegments)
    ? entry.flightSegments
    : [];
  if (segments.length < 2) {
    return [entry];
  }
  return segments.map((segment_id) => ({
    ...entry,
    flightSegments: [segment_id],
  }));
};

/**
 * Diff current selections against state captured at retrieve.
 * Selections not in committed → `to_add`; committed not in selections
 * → `to_remove`. Both lists carry the canonical shape
 * `{ travelerId, type, id, quantity, flightSegments }`.
 *
 * `included: true` entries (fare-bundle freebies — Kyte lists them with
 * no `pricingBreakdown` row) MUST stay in `committed_set` so a matching
 * selection isn't a duplicate `add`, but MUST NOT contribute to
 * `to_remove` — BookAncillaries rejects removals of fare-included
 * entries.
 * @param {Array<object>} committed
 * @param {object} selections
 * @returns {{to_add: Array<object>, to_remove: Array<object>}}
 */
export const compute_ancillary_diff = (committed, selections) => {
  const fingerprint = (entry) => {
    const segs = (entry.flightSegments || []).slice().sort().join(',');
    const qty = Number(entry.quantity) || 1;
    return `${entry.travelerId}|${entry.type}|${entry.id}|${segs}|${qty}`;
  };
  const committed_list = Array.isArray(committed) ? committed : [];
  const selected = flatten_selections(selections);
  const committed_set = new Set(
    committed_list
      .reduce((atoms, entry) => atoms.concat(segment_atoms(entry)), [])
      .map(fingerprint)
  );
  const selected_set = new Set(selected.map(fingerprint));
  // A journey-wide entry stays only while every leg is still selected.
  return {
    to_add: selected.filter((entry) => !committed_set.has(fingerprint(entry))),
    to_remove: committed_list
      .filter((entry) => !entry.included)
      .filter(
        (entry) =>
          !segment_atoms(entry).every((atom) =>
            selected_set.has(fingerprint(atom))
          )
      ),
  };
};

/**
 * The catalog id sold on `segment_id` for a committed `id`. easyJet's
 * retrieve folds a twin pair (`LUG` + `LUG_RT`) into one `LUG` row over every
 * leg, while the catalog sells `LUG_RT` on the return: resolve to the row on
 * that leg whose combineWith names `id`. Falls back to `id`.
 * @param {object} [slice] catalog slice for the entry's type
 * @param {string} id
 * @param {string} segment_id
 * @returns {string}
 */
const catalog_id_on_segment = (slice, id, segment_id) => {
  const safe = slice || {};
  if (safe[`${id}:${segment_id}`]) {
    return id;
  }
  const twin_key = Object.keys(safe).find((key) => {
    const { segmentIds = [], combineWith = [] } = safe[key] || {};
    return segmentIds.includes(segment_id) && combineWith.includes(id);
  });
  if (!twin_key) {
    return id;
  }
  return safe[twin_key].id;
};

/**
 * Re-key committed entries onto the catalog ids sold on each leg (see
 * catalog_id_on_segment), splitting a folded twin row per leg so it
 * fingerprints like the selections build_committed_selections seeds.
 * Entries the catalog already sells as-is pass through untouched.
 * @param {Array<object>} committed
 * @param {object} [catalog] fetch_ancillaries envelope, keyed by type
 * @returns {Array<object>}
 */
export const align_committed_to_catalog = (committed, catalog) =>
  (committed || []).reduce((out, entry) => {
    const { type = '', id = '', flightSegments = [] } = entry || {};
    const ids = flightSegments.map((segment_id) =>
      catalog_id_on_segment((catalog || {})[type], id, segment_id)
    );
    if (ids.every((leg_id) => leg_id === id)) {
      return out.concat(entry);
    }
    return out.concat(
      flightSegments.map((segment_id, index) => ({
        ...entry,
        id: ids[index],
        flightSegments: [segment_id],
      }))
    );
  }, []);

/**
 * Reconstruct the page-side `selections` map from a canonical FlightOffer.
 * Output keys match the canonical type strings the providers produce.
 * Bag-shaped buckets roll up by quantity per (pax, segment, ancillary id);
 * seats are keyed by (segment, pax) and carry the canonical entry as-is.
 *
 *     {
 *       bag:             { [travelerId]: { '${id}:${segId}': qty } },
 *       seat:            { [segId]: { [travelerId]: SeatEntry } },
 *       sportsEquipment: { [travelerId]: { '${id}:${segId}': qty } },
 *       service:         { [travelerId]: { '${id}:${segId}': qty } },
 *     }
 * @param {object} flight_offer
 * @param {object} [catalog] fetch_ancillaries envelope; keys resolve to the
 *   catalog id sold on each leg (see catalog_id_on_segment)
 * @returns {{bag: object, seat: object, sportsEquipment: object, service: object}}
 */
export const build_committed_selections = (flight_offer, catalog) => {
  const out = {
    bag: {},
    seat: {},
    sportsEquipment: {},
    service: {},
    meal: {},
    loungeAccess: {},
    disabilityAssistance: {},
  };
  const { travelerPricings = [] } = flight_offer || {};
  travelerPricings.forEach((pricing) => {
    const { travelerId = '', fareDetailsBySegment = [] } = pricing || {};
    if (!travelerId) return;
    fareDetailsBySegment.forEach((fare_detail) => {
      const { segmentId = '', additionalServices = [] } = fare_detail || {};
      if (!segmentId) return;
      const list = Array.isArray(additionalServices) ? additionalServices : [];
      list.forEach((entry) => {
        const {
          type = '',
          id = '',
          quantity = 1,
          seatNumber = '',
        } = entry || {};
        if (out[type] === undefined) return;
        if (type === 'seat') {
          if (!seatNumber) return;
          if (!out.seat[segmentId]) out.seat[segmentId] = {};
          out.seat[segmentId][travelerId] = { ...entry, number: seatNumber };
          return;
        }
        if (!id) return;
        const compound_key = `${catalog_id_on_segment(
          (catalog || {})[type],
          id,
          segmentId
        )}:${segmentId}`;
        if (!out[type][travelerId]) out[type][travelerId] = {};
        out[type][travelerId][compound_key] =
          (out[type][travelerId][compound_key] || 0) + (Number(quantity) || 1);
      });
    });
  });
  return out;
};
